import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { toast } from 'vue-sonner';
import useApi from '@/composables/useApi.ts';
import { uuid } from '@/lib/uuid.ts';

// The filter params of the documents page, as SearchController::getFilterParams() knows them.
const FILTER_PARAMS = [
    'documentType',
    'targetGroup',
    'consortiums',
    'featured',
];

// Shared state between composable calls
const query = ref('');
const matchingFiles = ref<string[]>([]);
const isPending = ref(false);

// Ties the keystrokes of one search to one row in the analytics search log, see useSearch.ts.
// A filter change reloads the page, so a page load always starts a new one.
const sequenceId = ref(uuid());

export default function useFileSearch(): {
    query: Ref<string>;
    reset: () => void;
    isPending: Ref<boolean>;
};
export default function useFileSearch(uid: string): {
    isVisible: ComputedRef<boolean>;
};
export default function useFileSearch(uid?: string) {
    const useFetch = useApi();

    async function performInlineSearch(query: string, silent = false) {
        const params = new URLSearchParams(window.location.search); // include filter params already present in the URL (e.g. "documentType")
        params.append('q', query);
        params.append('sequence', sequenceId.value);

        const { data, error } = await useFetch<string[]>(
            'search/inline?' + params.toString()
        );

        if (error.value) {
            if (silent) return;

            toast.error('Suchergebnisse konnten nicht geladen werden.');
            console.error(error.value);
            return;
        }

        matchingFiles.value = data.value as string[];
    }

    function reset() {
        sequenceId.value = uuid();
        query.value = '';
        isPending.value = false;
        matchingFiles.value = [];
    }

    if (!uid) {
        // When the composable gets called from within the Documents Layout, no uid is being passed.
        // This only registers the watchers once.

        watch(query, (newValue, oldValue) => {
            if (newValue !== oldValue) {
                isPending.value = true;
            }
        });

        watchDebounced(
            query,
            async (value) => {
                await performInlineSearch(value);
                isPending.value = false;
            },
            { debounce: 250 }
        );

        // Most filtering happens without typing: a checkbox reloads the page with the filter set,
        // and nothing reaches the search endpoint. Reporting the filtered page load as a search
        // with an empty term is what lets the analytics filter report see that use at all.
        const params = new URLSearchParams(window.location.search);

        if (FILTER_PARAMS.some((param) => params.has(`${param}[]`))) {
            performInlineSearch('', true);
        }

        return {
            query,
            reset,
            isPending,
        };
    }

    const isVisible = computed(() => {
        if (query.value === '') return true;

        return matchingFiles.value.includes(uid);
    });

    return {
        isVisible,
    };
}
